'use client';

/**
 * RecoveryModal Component
 *
 * Modal for executing fund recovery from abandoned protocols.
 * Handles both standard and emergency withdrawals.
 * Records successful recoveries for stats tracking.
 */
import { useState, useEffect, useRef } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Chip, Progress, Divider, Link } from '@heroui/react';
import { Shovel, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Coins, Gift, Loader2, PartyPopper, Shield, Eye, ArrowDownToLine } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useChainId, useAccount } from 'wagmi';
import { chainSettings, type ChainType } from '@/constants/chain-specific';
import { useFundRecovery } from '@/hooks/masterchef/useFundRecovery';
import { useUnderlyingAmounts } from '@/hooks/masterchef/useUnderlyingAmounts';
import { useTransactionSimulation } from '@/hooks/masterchef/useTransactionSimulation';
import type { UnderlyingAsset } from '@/lib/masterchef-cache/types';
interface StuckFundsPosition {
  protocol: {
    name: string;
    slug: string;
    status: string;
    statusReason?: string;
  };
  contract: {
    contractId: string;
    label: string;
    contractAddress: `0x${string}`;
    chainId: number;
  };
  poolId: number;
  lpTokenAddress: `0x${string}`;
  lpTokenSymbol?: string;
  lpTokenDecimals: number;
  stakedAmount: bigint;
  stakedAmountFormatted: string;
  pendingRewards?: bigint;
  underlyingAssets: UnderlyingAsset[];
  withdrawConfig: {
    standardWithdrawAbi: any;
    emergencyWithdrawAbi?: any;
    recommendation: 'standard' | 'emergency';
  };
}
interface RecoveryModalProps {
  position: StuckFundsPosition | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
type WithdrawMethod = 'standard' | 'emergency';
type ModalStep = 'choose' | 'simulate' | 'confirm' | 'pending' | 'success' | 'error';
export default function RecoveryModal({
  position,
  isOpen,
  onClose,
  onSuccess
}: RecoveryModalProps) {
  const chainId = useChainId();
  const {
    address: userAddress
  } = useAccount();
  const [step, setStep] = useState<ModalStep>('choose');
  const [selectedMethod, setSelectedMethod] = useState<WithdrawMethod | null>(null);
  const {
    withdraw,
    emergencyWithdraw,
    isWithdrawing,
    txHash,
    isSuccess,
    isError,
    error,
    reset
  } = useFundRecovery();

  // Transaction simulation for preview
  const {
    simulate,
    result: simulationResult,
    isSimulating,
    reset: resetSimulation
  } = useTransactionSimulation();

  // Get underlying amounts for recovery stats
  const {
    amounts: underlyingAmounts
  } = useUnderlyingAmounts({
    lpTokenAddress: position?.lpTokenAddress || '0x0',
    userLpBalance: position?.stakedAmount || 0n,
    underlyingAssets: position?.underlyingAssets || [],
    chainId: position?.contract.chainId || chainId,
    enabled: !!position && position.underlyingAssets.length > 0
  });

  // Track if we've already submitted this recovery
  const recoverySubmittedRef = useRef<string | null>(null);

  // Get chain config for explorer links
  const chain = position ? chainSettings[position.contract.chainId as ChainType] : null;
  const explorerUrl = chain?.blockExplorerUrl;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('choose');
      setSelectedMethod(null);
      reset();
      recoverySubmittedRef.current = null;
    }
  }, [isOpen, reset]);

  // Submit recovery stats when transaction succeeds
  useEffect(() => {
    if (isSuccess && txHash && position && underlyingAmounts.length > 0 && recoverySubmittedRef.current !== txHash) {
      // Mark as submitted to prevent duplicates
      recoverySubmittedRef.current = txHash;

      // Submit recovery stats
      const submitRecovery = async () => {
        try {
          const tokens = underlyingAmounts.map(({
            asset,
            amount
          }) => ({
            address: asset.address,
            symbol: asset.symbol,
            decimals: asset.decimals,
            amount: amount.toString()
          }));
          await fetch('/api/masterchef/recovery/record', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              chainId: position.contract.chainId,
              txHash,
              tokens
            })
          });
          console.log('[RecoveryModal] Recovery stats submitted');
        } catch (err) {
          console.error('[RecoveryModal] Failed to submit recovery stats:', err);
          // Don't block the user flow if stats submission fails
        }
      };
      submitRecovery();
    }
  }, [isSuccess, txHash, position, underlyingAmounts]);

  // Handle transaction state changes
  useEffect(() => {
    if (isWithdrawing && step !== 'pending') {
      setStep('pending');
    }
    if (isSuccess && step === 'pending') {
      setStep('success');
      // Trigger parent callback after a delay
      setTimeout(() => {
        onSuccess();
      }, 2000);
    }
    if (isError && step === 'pending') {
      setStep('error');
    }
  }, [isWithdrawing, isSuccess, isError, step, onSuccess]);

  // Handle method selection - trigger simulation
  const handleSelectMethod = async (method: WithdrawMethod) => {
    if (!position || !userAddress) return;
    setSelectedMethod(method);
    setStep('simulate');
    resetSimulation();

    // Get the appropriate ABI
    const abi = method === 'standard' ? position.withdrawConfig.standardWithdrawAbi : position.withdrawConfig.emergencyWithdrawAbi;

    // Run simulation
    await simulate({
      contractAddress: position.contract.contractAddress,
      poolId: position.poolId,
      userAddress,
      lpTokenAddress: position.lpTokenAddress,
      lpTokenSymbol: position.lpTokenSymbol || 'LP',
      lpTokenDecimals: position.lpTokenDecimals,
      stakedAmount: position.stakedAmount,
      withdrawType: method,
      abi,
      chainId: position.contract.chainId
    });
  };

  // Proceed from simulation to confirm
  const handleProceedToConfirm = () => {
    setStep('confirm');
  };

  // Execute withdraw
  const handleExecute = () => {
    if (!position || !selectedMethod) return;
    if (selectedMethod === 'standard') {
      withdraw(position.contract.contractAddress, position.poolId, position.stakedAmount, position.withdrawConfig.standardWithdrawAbi);
    } else {
      emergencyWithdraw(position.contract.contractAddress, position.poolId, position.withdrawConfig.emergencyWithdrawAbi);
    }
  };

  // Handle retry
  const handleRetry = () => {
    reset();
    setStep('choose');
    setSelectedMethod(null);
  };
  if (!position) return null;
  const isEmergencyRecommended = position.withdrawConfig.recommendation === 'emergency';
  const hasEmergencyOption = !!position.withdrawConfig.emergencyWithdrawAbi;
  const displayAmount = Number(position.stakedAmountFormatted) > 0.0001 ? Number(position.stakedAmountFormatted).toFixed(4) : '<0.0001';
  return <Modal isOpen={isOpen} onClose={step === 'pending' ? undefined : onClose} isDismissable={step !== 'pending'} hideCloseButton={step === 'pending'} size="lg" classNames={{
    base: 'bg-content1 border border-divider',
    header: 'border-b border-divider'
  }}>
      <ModalContent>
        {onCloseModal => <>
            <ModalHeader className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
                <Shovel className="w-5 h-5 text-warning" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Rescue Your Funds</h3>
                <p className="text-sm text-foreground-500 font-normal">
                  {position.protocol.name} · Pool #{position.poolId}
                </p>
              </div>
            </ModalHeader>

            <ModalBody className="py-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Choose Method */}
                {step === 'choose' && <motion.div key="choose" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-4">
                    {/* Position Summary */}
                    <div className="p-4 bg-content2 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Coins className="w-8 h-8 text-warning" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold">{displayAmount}</span>
                            <span className="text-foreground-500">
                              {position.lpTokenSymbol || 'LP Tokens'}
                            </span>
                          </div>
                          {position.underlyingAssets.length > 0 && <p className="text-sm text-foreground-400">
                              {position.underlyingAssets.map(a => a.symbol).join(' / ')}
                            </p>}
                        </div>
                      </div>
                    </div>

                    {/* Warning for emergency recommendation */}
                    {isEmergencyRecommended && <div className="flex items-start gap-2 p-3 bg-danger/10 rounded-lg border border-danger/30">
                        <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0" />
                        <p className="text-sm text-danger">
                          Emergency withdraw is recommended for this protocol due to potential
                          contract issues.
                        </p>
                      </div>}

                    {/* Method Options */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Choose rescue method:</p>

                      {/* Standard Withdraw */}
                      <button type="button" onClick={() => handleSelectMethod('standard')} className={`
                          w-full p-4 rounded-xl border-2 text-left transition-all
                          hover:border-success hover:bg-success/5
                          ${isEmergencyRecommended ? 'border-divider opacity-75' : 'border-success/50'}
                        `}>
                        <div className="flex items-start gap-3">
                          <Gift className="w-6 h-6 text-success flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold">Standard Withdraw</span>
                              {!isEmergencyRecommended && <Chip size="sm" color="success" variant="flat">
                                  Recommended
                                </Chip>}
                            </div>
                            <p className="text-sm text-foreground-500 mt-1">
                              Withdraw your LP tokens and claim any pending rewards.
                            </p>
                          </div>
                        </div>
                      </button>
                      {/* Emergency Withdraw */}
                      {hasEmergencyOption && <button type="button" onClick={() => handleSelectMethod('emergency')} className={`
                            w-full p-4 rounded-xl border-2 text-left transition-all
                            hover:border-warning hover:bg-warning/5
                            ${isEmergencyRecommended ? 'border-warning/50' : 'border-divider'}
                          `}>   
                        
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold">Emergency Withdraw</span>
                                {isEmergencyRecommended && <Chip size="sm" color="warning" variant="flat">
                                    Recommended
                                  </Chip>}
                              </div>
                              <p className="text-sm text-foreground-500 mt-1">
                                Withdraw LP tokens only. Forfeits any pending rewards but
                                bypasses potential contract issues.
                              </p>
                            </div>
                          </div>
                        </button>}
                    </div>
                  </motion.div>}

                {/* Step 2: Simulation Preview */}
                {step === 'simulate' && selectedMethod && <motion.div key="simulate" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-4">
                    {/* Simulating State */}
                    {isSimulating && <div className="text-center py-8">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-12 h-12 mx-auto mb-4">
                          <Eye className="w-12 h-12 text-primary" />
                        </motion.div>
                        <h4 className="text-lg font-bold mb-2">Simulating Transaction...</h4>
                        <p className="text-sm text-foreground-500">
                          Previewing what will happen when you execute this rescue
                        </p>
                      </div>}

                    {/* Simulation Success */}
                    {!isSimulating && simulationResult?.willSucceed && <div className="space-y-4">
                        <div className="flex items-center gap-3 p-4 bg-success/10 rounded-xl border border-success/30">
                          <Shield className="w-8 h-8 text-success flex-shrink-0" />
                          <div>
                            <h4 className="font-bold text-success">Transaction Will Succeed</h4>
                            <p className="text-sm text-foreground-500">
                              Simulation verified - your rescue is safe to execute
                            </p>
                          </div>
                        </div>

                        {/* Asset Changes Preview */}
                        <div className="p-4 bg-content2 rounded-xl space-y-3">
                          <h5 className="text-sm font-medium text-foreground-500">You will receive:</h5>
                          {simulationResult.assetChanges.map(change => <div key={`${change.token}-${change.direction}`} className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                                <ArrowDownToLine className="w-4 h-4 text-success" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">
                                    {Number(change.amountFormatted) > 0.0001 ? Number(change.amountFormatted).toFixed(4) : '<0.0001'}{' '}
                                    {change.symbol}
                                  </span>
                                  <Chip size="sm" color="success" variant="flat">
                                    Incoming
                                  </Chip>
                                </div>
                              </div>
                            </div>)}
                        </div>

                        {/* Warnings */}
                        {simulationResult.warnings.length > 0 && <div className="p-3 bg-warning/10 rounded-lg border border-warning/30">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                              <div className="text-sm text-warning">
                                {simulationResult.warnings.map(warning => <p key={warning}>{warning}</p>)}
                              </div>
                            </div>
                          </div>}
                      </div>}

                    {/* Simulation Failed */}
                    {!isSimulating && simulationResult && !simulationResult.willSucceed && <div className="space-y-4">
                        <div className="flex items-center gap-3 p-4 bg-danger/10 rounded-xl border border-danger/30">
                          <XCircle className="w-8 h-8 text-danger flex-shrink-0" />
                          <div>
                            <h4 className="font-bold text-danger">Transaction Will Fail</h4>
                            <p className="text-sm text-foreground-400">
                              {simulationResult.error || 'The simulation indicates this transaction would revert'}
                            </p>
                          </div>
                        </div>

                        {/* Suggestions */}
                        {selectedMethod === 'standard' && hasEmergencyOption && <div className="p-3 bg-warning/10 rounded-lg border border-warning/30">
                            <p className="text-sm text-warning">
                              <strong>Tip:</strong> Try emergency withdraw instead - it may bypass the issue.
                            </p>
                          </div>}
                      </div>}
                  </motion.div>}

                {/* Step 3: Confirm */}
                {step === 'confirm' && selectedMethod && <motion.div key="confirm" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-4">
                    <div className="text-center py-4">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warning/20 flex items-center justify-center">
                        <Shovel className="w-8 h-8 text-warning" />
                      </div>
                      <h4 className="text-xl font-bold mb-2">Confirm Rescue</h4>
                      <p className="text-foreground-500">
                        You are about to{' '}
                        {selectedMethod === 'emergency' ? 'emergency ' : ''}
                        withdraw your funds.
                      </p>
                    </div>

                    <Divider />

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-500">Amount</span>
                        <span className="font-medium text-white">
                          {displayAmount} {position.lpTokenSymbol || 'LP'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">Method</span>
                        <Chip size="sm" color={selectedMethod === 'emergency' ? 'warning' : 'success'} variant="flat">
                          {selectedMethod === 'emergency' ? 'Emergency' : 'Standard'}
                        </Chip>
                      </div>
                      {selectedMethod === 'emergency' && <div className="flex justify-between">
                          <span className="text-foreground-500">Pending Rewards</span>
                          <span className="text-danger">Forfeited</span>
                        </div>}
                      <div className="flex justify-between">
                        <span className="text-foreground-500">Contract</span>
                        <span className="font text-xs text-white">
                          {position.contract.contractAddress.slice(0, 6)}...
                          {position.contract.contractAddress.slice(-4)}
                        </span>
                      </div>
                    </div>
                  </motion.div>}

                {/* Step 3: Pending */}
                {step === 'pending' && <motion.div key="pending" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="text-center py-8">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 mx-auto mb-4">
                      <Loader2 className="w-16 h-16 text-warning" />
                    </motion.div>
                    <h4 className="text-xl font-bold mb-2 text-white">Rescuing Your Funds...</h4>
                    <p className="text-foreground-500 mb-4">
                      Please confirm the transaction in your wallet
                    </p>
                    {txHash && <div className="space-y-2">
                        <Progress isIndeterminate size="sm" color="warning" className="max-w-xs mx-auto" />
                        <p className="text-sm text-foreground-400">
                          Waiting for confirmation...
                        </p>
                        {explorerUrl && <Link href={`${explorerUrl}/tx/${txHash}`} isExternal showAnchorIcon size="sm" className="text-white">
                            View transaction
                          </Link>}
                      </div>}
                  </motion.div>}

                {/* Step 4: Success */}
                {step === 'success' && <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} className="text-center py-8">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                delay: 0.1
              }} className="w-20 h-20 mx-auto mb-4 rounded-full bg-success/20 flex items-center justify-center">
                      <PartyPopper className="w-10 h-10 text-success" />
                    </motion.div>
                    <h4 className="text-2xl font-bold text-success mb-2">Funds Rescued!</h4>
                    <p className="text-foreground-500 mb-4">
                      Your {displayAmount} {position.lpTokenSymbol || 'LP tokens'} have been
                      safely recovered!
                    </p>
                    {txHash && explorerUrl && <Link href={`${explorerUrl}/tx/${txHash}`} isExternal showAnchorIcon color="success">
                        View transaction
                      </Link>}
                  </motion.div>}

                {/* Step 5: Error */}
                {step === 'error' && <motion.div key="error" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger/20 flex items-center justify-center">
                      <XCircle className="w-10 h-10 text-danger" />
                    </div>
                    <h4 className="text-xl font-bold text-danger mb-2">Rescue Failed</h4>
                    <p className="text-foreground-500 mb-4">
                      {error?.message || 'Something went wrong. Please try again.'}
                    </p>
                    {isEmergencyRecommended && selectedMethod === 'standard' && <p className="text-sm text-warning">
                        Tip: Try emergency withdraw if standard withdraw fails.
                      </p>}
                  </motion.div>}
              </AnimatePresence>
            </ModalBody>

            <ModalFooter>
              {step === 'choose' && <Button variant="flat" onPress={onCloseModal} className="text-black">
                  Cancel
                </Button>}

              {step === 'simulate' && <>
                  <Button variant="flat" onPress={() => {
              setStep('choose');
              setSelectedMethod(null);
              resetSimulation();
            }} className="text-black" isDisabled={isSimulating}>
                    Back
                  </Button>
                  {!isSimulating && simulationResult?.willSucceed && <Button color="success" onPress={handleProceedToConfirm} startContent={<CheckCircle2 className="w-4 h-4" />} className="text-black">
                      Continue to Confirm
                    </Button>}
                  {!isSimulating && simulationResult && !simulationResult.willSucceed && <>
                      <Button color="warning" variant="flat" onPress={() => {
                setStep('choose');
                setSelectedMethod(null);
                resetSimulation();
              }}>
                        Try Different Method
                      </Button>
                      <Button color="danger" variant="bordered" onPress={handleProceedToConfirm} startContent={<AlertTriangle className="w-4 h-4" />}>
                        Try Anyway
                      </Button>
                    </>}
                </>}

              {step === 'confirm' && <>
                  <Button variant="flat" onPress={() => setStep('choose')} className="text-black">
                    Back
                  </Button>
                  <Button color={selectedMethod === 'emergency' ? 'warning' : 'success'} onPress={handleExecute} startContent={<Shovel className="w-4 h-4" />} className="text-black">
                    Confirm Rescue
                  </Button>
                </>}

              {step === 'success' && <Button color="success" onPress={onCloseModal}>
                  Done
                </Button>}

              {step === 'error' && <>
                  <Button variant="flat" color="warning" onPress={onCloseModal} className="text-black">
                    Close
                  </Button>
                  <Button color="warning" onPress={handleRetry} className="text-black">
                    Try Again
                  </Button>
                </>}
            </ModalFooter>
          </>}
      </ModalContent>
    </Modal>;
}